import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnDestroy, PLATFORM_ID } from '@angular/core';
import { delay, interval, of, Subscription, switchMap } from 'rxjs';
import { genTinyGUID } from '@cudirect/shared-ui-library';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { BuildingBlock, BuildingBlockControlBaseComponent } from '../building-block.types';
import { ApiResult, ApiErrorResult } from '@workflow/ecp-workflow/dynamic-app/service/application-api/application-api.types';
import { InstaTouchUserInformation } from '@workflow/ecp-workflow/dynamic-app/service/instaTouch/insta-touch.types';
import { InstaTouchState, InstaTouchSession } from '@shared/model/instatouch.model';
import { LoanType } from '@shared/enum';
import { FlowType } from '@workflow/ecp-shared/model/types';
import { UserInfo } from '@workflow/ecp-shared/model/userinfo';
import { ConsumerLogin } from 'src/user/model/consumer-login';
import { OIDLoginWidgetExportData } from '@cudirect/ea-login';
import { isPlatformBrowser } from '@angular/common';
import { BundleHelperService } from '@workflow/ecp-workflow/services/bundle-helper.service';

interface GetLoginDataResponse {
  oldSession: string;
  user: ConsumerLogin;
  prevSession: string;
  comingFromHeavyReg: string;
}

interface GetLoginDataProps {
  email: string;
  phoneNumber: string;
  accessToken: string;
  refreshToken: string;
  ssoLogin?: boolean;
  idToken?: string;
  expiresIn?: string;
  authCode?: string;
  extToken?: string;
  discoveryURL?: string;
  loginMode: 'login' | 'heavyReg' | 'verify';
}

interface HandleReceivedDataForLoginModeResponse {
  phoneNumber?: string;
  email?: string;
}

/**
 * Normalized applicant context so OAO & OLA logic stays structurally identical.
 * (Intentionally keeps index mapping logic centralized to avoid drift.)
 */
interface ApplicantContext {
  prefix: 'api' | 'ola';
  applicantIndex: number;
  /** Full stateful key for entity token */
  entityTokenKey: string;
  /** Full stateful key for VFP (verify) code */
  verifyCodeKey: string;
  /** Base path (WITH prefix) to the applicant root (borrowerInfo / oaoMemberInformation/<segment>) */
  basePath: string; // e.g. api:oaoMemberInformation/primaryApplicant | ola:borrowerInfo
  /** Phone/mobile key (WITH prefix) for persistence */
  phoneKey: string; // e.g. api:oaoMemberInformation/primaryApplicant/mobileNumber | ola:borrowerInfo/primaryPhoneNumber
}

/** this component helps to retrive personal information using mobile number */
@BuildingBlock('personal-info-retrieval')
@Component({
  selector: 'ecp-autofill-application',
  templateUrl: './autofill-application.component.html',
  standalone: false
})
export class AutofillApplicationComponent extends BuildingBlockControlBaseComponent implements OnDestroy {
  protected logger = inject(SawDust);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  /** Subscription for alloy verify code polling */
  private alloyVerifyPollSub: Subscription;

  /** Widget Export Data */
  public exportData: InstaTouchUserInformation;

  private readonly bundleHelperService = inject(BundleHelperService);
  /** currentStage */
  public currentStage: string;

  /** flag to indicate Api call in progress */
  public isLoading: boolean;

  /** Instatouch Session */
  public instaTouchSessionId: string;

  /** Instatouch error message */
  public errorMessage: string;

  /** instaTouch stage */
  public stage: InstaTouchState = 'initial';

  /** mobileNumber (made public for template binding to pass into dx-prefill) */
  public mobileNumber: string;
  public showCodeSentAlert = false;

  /** Page-context flags to derive applicant index (mirrors logic in PrefillComponent) */
  private isCoapplicant = false;
  private isSecondCoapplicant = false;

  /** Compute applicant index: 1 = primary, 2 = first co-applicant, 3 = second co-applicant */
  private get applicantIndex(): number {
    if (this.isSecondCoapplicant) {
      return 3;
    }
    if (this.isCoapplicant) {
      return 2;
    }
    return 1;
  }

  public get isAlloyPrefill(): boolean {
    return this.lenderService?.lenderData?.enableAlloyPrefill;
  }
  public get flowType(): FlowType {
    if (!this.userService?.flowType) {
      this.logger.info('flowType is not set in autofill application component', { component: 'AutofillApplicationComponent' });
    }
    return this.userService?.flowType;
  }

  /** Hold the value to Show and hide OAO Flow texts. */
  public get isOaoFlow() {
    return this.flowType.toLowerCase() === LoanType.OAO.toLowerCase();
  }
  /** Hold the value to Show and hide OLA Flow texts. */
  public get isOlaFlow() {
    return this.flowType.toLowerCase().toLowerCase() === LoanType.OLA.toLowerCase();
  }

  /** Lifecycle hook: derive applicant context then restore Alloy prefill state for THAT applicant only */
  public ngOnInit(): void {
    // Derive page-based applicant role (similar to PrefillComponent constructor logic)
    const page = this.workflowService.currentPage;
    const pageId = page?.config?.id?.toLowerCase() || '';
    this.isCoapplicant = pageId.includes('co-applicant') && !pageId.includes('additional-co-app');
    this.isSecondCoapplicant = pageId.includes('additional-co-app');

    if (!this.isAlloyPrefill) return; // Only applies to alloy prefill journeys

    const ctx = this.getApplicantContext(this.applicantIndex);
    const token = this.statefulService.wrap(ctx.entityTokenKey).valueString;
    if (token && this.stage === 'initial') {
      const storedPhone = this.statefulService.wrap(ctx.phoneKey).valueString;
      if (storedPhone) this.mobileNumber = storedPhone;
      this.stage = 'passcode';
      this.startAlloyVerifyCodePolling(this.applicantIndex);
    }
  }
  /**
   * Unified helper to describe applicant-specific keys & base paths across OAO and OLA.
   * Ensures both flows evolve together and remain structurally identical.
   */
  private getApplicantContext(applicantIndex: number): ApplicantContext {
    const isOla = this.isOlaFlow;
    const prefix: 'api' | 'ola' = isOla ? 'ola' : 'api';

    // Base path mapping
    let basePath: string;
    if (isOla) {
      if (applicantIndex === 1) {
        basePath = 'ola:borrowerInfo';
      } else {
        // Map 2 -> coBorrowers/0, 3 -> coBorrowers/1
        const coIndex = applicantIndex - 2; // 2 -> 0, 3 -> 1
        basePath = `ola:coBorrowers/${coIndex}/borrowerInfo`;
      }
    } else {
      if (applicantIndex === 1) {
        basePath = 'api:oaoMemberInformation/primaryApplicant';
      } else if (applicantIndex === 2) {
        basePath = 'api:oaoMemberInformation/coApplicant';
      } else {
        basePath = 'api:oaoMemberInformation/additionalCoApplicant';
      }
    }

    // Phone key path
    const phoneKey = isOla ? `${basePath}/primaryPhoneNumber` : `${basePath}/mobileNumber`;

    // Entity token & verify code keys (journeyInfo based, identical pattern)
    const entityTokenKey =
      applicantIndex === 1 ? `${prefix}:journeyInfo/applicantEntityToken` : `${prefix}:journeyInfo/applicant${applicantIndex}EntityToken`;
    const verifyCodeKey =
      applicantIndex === 1 ? `${prefix}:journeyInfo/applicantVfp` : `${prefix}:journeyInfo/applicant${applicantIndex}Vfp`;

    return { prefix, applicantIndex, entityTokenKey, verifyCodeKey, basePath, phoneKey };
  }

  /** Passcode */
  public getPassCode({ mobileNumber, ssn, applicantIndex }: InstaTouchSession): void {
    this.isLoading = true;
    this.errorMessage = null;
    if (this.preCheck(mobileNumber)) {
      // if(this.field.data['backend']['data']['api:borrowerInfo/primaryPhoneNumber'] === event.mobileNumber){
      this.isLoading = false;
      this.errorMessage = "You can't use the same number for multiple applicants. Please use a different number.";
    } else {
      const onSuccess = (response: ApiResult<any>): void => {
        // this.field.data.value = event.mobileNumber;

        this.mobileNumber = mobileNumber;

        // autofill enabled for multiple applicants, so its not require here, otherwise overrides value
        // else {
        //     this.statefulService.wrap(`api:oaoMemberInformation/primaryApplicant/mobileNumber`).value = event.mobileNumber;
        // }

        if (response.isSuccess) {
          if (this.stage === 'passcode') this.showCodeSentAlert = true;
          this.statefulService.wrap('ss:oao-primary-applicant-prefill-alert-success').value = null;
          this.statefulService.wrap('ss:oao-primary-co-applicant-prefill-alert-success').value = null;
          this.statefulService.wrap('ss:oao-primary-additional-co-applicant-prefill-alert-success').value = null;
          this.instaTouchSessionId = response.data?.AccessToken ?? '';
          this.stage = 'passcode';
          this.isLoading = false;
        } else {
          // 200 response but logical failure
          const key = this.isAlloyPrefill
            ? 'personalInfoAutofill/errorGeneratingPasscode/alloy'
            : 'personalInfoAutofill/errorGeneratingPasscode/instatouch';
          this.errorMessage = this.dictionary.read(key);
          this.isLoading = false;
        }
      };
      const onError = (err: HttpErrorResponse): void => {
        const passcodeErrorKey = this.isAlloyPrefill
          ? 'personalInfoAutofill/errorGeneratingPasscode/alloy'
          : 'personalInfoAutofill/errorGeneratingPasscode/instatouch';
        this.errorHandler(err, passcodeErrorKey);
      };

      if (this.isAlloyPrefill) {
        this.applicationApiService
          .generatePrefillLink({
            phoneNumber: mobileNumber,
            last4Ssn: ssn?.toString(),
            countryPrefix: '+1',
            applicantIndex: applicantIndex
          })
          .subscribe({
            next: (res) => {
              if (!res.isSuccess) {
                onError({ error: {} } as HttpErrorResponse);
                return;
              }
              onSuccess(res);
              // Persist EntityToken (entityToken) for restore logic if available
              try {
                if (res?.data?.entityToken) {
                  const ctx = this.getApplicantContext(applicantIndex);
                  this.statefulService.wrap(ctx.entityTokenKey).value = res.data.entityToken;
                }
              } catch {}
              this.startAlloyVerifyCodePolling(applicantIndex);
            },
            error: onError
          });
      } else {
        this.instaTouchService.generatePasscode(mobileNumber ? mobileNumber.toString() : '').subscribe(onSuccess, onError);
      }
    }
  }

  /** Polls stateful storage for the applicant's verify code (applicantVfp keys) after link generation. */
  private startAlloyVerifyCodePolling(applicantIndex: number): void {
    // Clear any previous polling
    if (this.alloyVerifyPollSub) {
      return; // already polling we do this once
    }
    const key = this.getApplicantContext(applicantIndex).verifyCodeKey;

    this.statefulService.wrap('mem:alloy-prefill-inprogress').value = true;

    // - Delay polling start 15s
    // - Refresh bundle from server each interval
    // - Interval every 6s
    // - Max attempts 30 (~3 minutes total)
    const intervalMs = 6000;
    const maxAttempts = 30;
    const delayMs = 15000; // initial delay before first bundle fetch

    this.alloyVerifyPollSub = of(1)
      .pipe(
        delay(delayMs),
        switchMap(() => interval(intervalMs))
      )
      .subscribe((attempts) => {
        // Refresh bundle to see if it has vfy code
        try {
          if (this.isOlaFlow) {
            this.bundleHelperService.refreshOLABundle();
          } else {
            this.bundleHelperService.refreshBundle();
          }
        } catch (e) {
          // non-fatal for polling; continue attempts
        }
        const code = this.statefulService.wrap(key).valueString;
        // Only exit loop on success (code found) or after maxAttempts; continue otherwise.
        if (code) {
          this.logger.logEvent('ecp.ui.alloy.prefill', {
            eventState: 'end',
            correlationId: key,
            message: 'prefill-verified-success'
          });
          this.alloyVerifyPollSub.unsubscribe();
          this.handleAlloyVerifyCodeDetected({ applicantIndex });
        } else if (attempts >= maxAttempts) {
          this.logger.logEvent('ecp.ui.alloy.prefill', {
            eventState: 'end',
            correlationId: key,
            message: 'prefill-verified-timeout'
          });
          this.alloyVerifyPollSub.unsubscribe();
        }
      });
  }

  /** Returns the stateful storage key for the applicant verify (VFP) code; OLA uses ola: prefix, OAO (default) uses api: */
  // (verifyCodeKey now provided via getApplicantContext)

  /**
   * Invoked when an Alloy verify code (VFP) has appeared in stateful storage.
   * The backend has already validated & associated the code with the journey by the time
   * it is surfaced here (the polling only refreshes bundle state). Therefore we should NOT
   * call the verify endpoint again; simply mark autofill success, refresh bundle once, and
   * advance the UI stage consistent with the Instatouch flow after passcode verification.
   */
  private handleAlloyVerifyCodeDetected({ applicantIndex }: { applicantIndex: number }): void {
    const correlationId = `verifyCode:${applicantIndex}`;
    this.logger.logEvent('ecp.ui.alloy.prefill', {
      eventState: 'start',
      correlationId,
      message: 'code-detected'
    });

    // Attempt to complete alloy prefill using data already populated in bundle
    this.completeAlloyPrefillFromBundle(applicantIndex, correlationId);
  }

  /**
   * Completes the Alloy prefill flow using data presumed to be populated in the application bundle
   * after the verify code was processed on the server. This mirrors the success branch of getPersonalInfo
   * without re-calling Instatouch APIs or the Alloy verify endpoint.
   */
  private async completeAlloyPrefillFromBundle(applicantIndex: number, correlationId: string): Promise<void> {
    const eventPairId = genTinyGUID(24);
    this.logEvent('start', eventPairId);
    this.isLoading = true;

    try {
      const finalize = () => {
        this.onClick(new Event('click'));
        this.stage = 'completed';
        this.isLoading = false;
        this.logEvent('end', eventPairId, 'success');
        this.logger.logEvent('ecp.ui.alloy.prefill', {
          eventState: 'end',
          correlationId,
          message: 'advance-completed'
        });
        this.statefulService.wrap('mem:alloy-prefill-inprogress').value = false;
      };

      if (applicantIndex === 1) {
        const verifyCodeKey = this.getApplicantContext(1).verifyCodeKey;
        const verifyCode = this.statefulService.wrap(verifyCodeKey).valueString;
        if (verifyCode && verifyCode !== 'skipped') {
          const completionCorrelationId = `primary-complete:${applicantIndex}`;
          this.logger.logEvent('ecp.ui.alloy.prefill', {
            eventState: 'start',
            correlationId: completionCorrelationId,
            message: 'primary-complete-dispatch'
          });
          this.applicationApiService.completePrimaryAlloyPrefill({ verifyCode }).subscribe({
            next: (res) => {
              this.logger.logEvent('ecp.ui.alloy.prefill', {
                eventState: 'end',
                correlationId: completionCorrelationId,
                message: res?.isSuccess ? 'primary-complete-success' : `primary-complete-failed|${res?.reason || 'no-reason'}`
              });
              const token = res?.data;
              if (res?.isSuccess && token?.access_token && token?.refresh_token && !this.userService.isAuthenticated) {
                // this.userService.isExistingUser = !!res?.data?.isExistingUser;
                const loginType = this.statefulService.wrap(`${this.isOaoFlow ? 'api' : 'ola'}:loginType`).value as string;
                const { access_token: accessToken, refresh_token: refreshToken } = token;
                this.tokenService.updateToken({
                  access_token: accessToken,
                  refresh_token: refreshToken,
                  expires_in: 0,
                  id_token: null,
                  token_type: null
                });
                const loginMode = 'verify';
                this.setupTokenInitialize()
                  .then(() => this.setUpVerifyFlow({ refreshToken, accessToken }))
                  .then((r) => this.receivedDataHandler(r))
                  .then((r) =>
                    this.handleReceivedDataForLoginMode(r).then((handled) =>
                      this.getLoginData({
                        loginMode,
                        email: handled.email,
                        phoneNumber: handled.phoneNumber,
                        accessToken,
                        refreshToken,
                        ssoLogin: false
                      })
                    )
                  )
                  .then((loginData) => {
                    const { oldSession, user, comingFromHeavyReg } = loginData;
                    let ipromise;
                    if (oldSession && !comingFromHeavyReg && this.storageService.isAllBackendInitialized()) {
                      ipromise = this.getLoginDataContinued({ oldSession });
                    } else {
                      ipromise = this.storageService.initAllBackends().then(() => this.getLoginDataContinued({ oldSession }));
                    }
                    return ipromise
                      .then(() => this.setUser({ user, oldSession, loginType }))
                      .then(({ userInfo }) =>
                        this.afterLogin({ userInfo, loginMode }).then(() => {
                          if (this.userService.user?.loginType && this.userService.user.loginType.toLowerCase() === 'oao') {
                            return this.setOAOMember({ userInfo });
                          }
                        })
                      );
                  })
                  .catch((err: HttpErrorResponse) => this.errorHandler(err, 'getPersonalInfo/instatouch'))
                  .finally(() => finalize());
              } else {
                finalize();
              }
            },
            error: () => {
              this.logger.logEvent('ecp.ui.alloy.prefill', {
                eventState: 'end',
                correlationId: completionCorrelationId,
                message: 'primary-complete-error'
              });
              finalize();
            }
          });
          return; // Defer finalization to observable handlers
        }
      }

      // Non-primary applicant finalize immediately
      finalize();
    } catch (e) {
      this.isLoading = false;
      this.logEvent('end', eventPairId, 'error');
      this.logger.logEvent('ecp.ui.alloy.prefill', {
        eventState: 'end',
        correlationId,
        message: 'completion-error'
      });
    }
  }

  /** Resend passcode / prefill link (Alloy uses different endpoint) */
  public resendPassCode({ mobileNumber, ssn, applicantIndex }: InstaTouchSession): void {
    // For non-alloy just reuse original flow
    if (!this.isAlloyPrefill) {
      this.getPassCode({ mobileNumber, ssn, applicantIndex } as InstaTouchSession);
      return;
    }
    this.isLoading = true;
    this.errorMessage = null;
    const onSuccess = (response: ApiResult<boolean>): void => {
      if (response.isSuccess) {
        this.showCodeSentAlert = true;
      } else {
        const key = 'personalInfoAutofill/errorGeneratingPasscode/alloy';
        this.errorMessage = this.dictionary.read(key);
      }
      this.isLoading = false;
    };
    const onError = (err: HttpErrorResponse): void => {
      const passcodeErrorKey = 'personalInfoAutofill/errorGeneratingPasscode/alloy';
      this.errorHandler(err, passcodeErrorKey);
    };
    // Need entityId (application id) for resend endpoint payload

    this.applicationApiService
      .resendPrefillLink({
        applicantIndex: applicantIndex
      })
      .subscribe(onSuccess, onError);
  }

  /** Validate Passcode */
  public validatePassCode({ passCode }: InstaTouchSession): void {
    this.isLoading = true;
    this.errorMessage = null;
    this.showCodeSentAlert = false;
    this.instaTouchService.validatePasscode(this.instaTouchSessionId, passCode ? passCode.toString() : '', '').subscribe({
      next: (response: ApiResult<boolean>) => {
        if (response.data && response.isSuccess) {
          this.stage = 'identity';
          this.isLoading = false;
        } else {
          this.errorHandler({} as HttpErrorResponse, 'personalInfoAutofill/errorValidatingPasscode');
        }
      },
      error: (err: HttpErrorResponse) => {
        this.errorHandler(err, 'personalInfoAutofill/errorValidatingPasscode');
      }
    });
  }

  /**
   * Initializes the token service with configuration settings and sets up caching for user session tokens.
   *
   * @remarks
   * This method is used to initialize the token service with necessary configuration and API keys.
   * It also configures the caching mechanism for storing user session tokens in either `sessionStorage`
   * or another specified storage type.
   *
   * @returns A promise that resolves once the token service is successfully initialized and configured.
   */
  private async setupTokenInitialize(): Promise<void> {
    return this.tokenService
      .initialize(
        this.configService?.settings?.oid.apiBaseURL,
        this.xApiKeyService.createApiHeader(),
        this.lenderService.lenderDataInfo?.oidConfigName ? this.lenderService.lenderDataInfo.oidConfigName : this.configService.settings?.oid.oidConfigName,
        true
      )
      .then(() => {
        this.tokenService.configureCache(this.configService?.settings?.arcDXTokenStorageType || 'sessionStorage', 'ecp-user-session');
      });
  }

  private async setUpVerifyFlow({
    accessToken,
    refreshToken
  }: {
    accessToken: string;
    refreshToken: string;
  }): Promise<OIDLoginWidgetExportData> {
    // This function is named the same as that found in login component.
    // Original parameters: none

    // this grabbed the user from userService that we create in this
    //   flow, but we aren't going to do it now since we have one
    //   created now from the mfa skip login flow
    // const userInfo = this.userService.user;
    // this.userId = phonenumber; // only used to track heavy reg in login component
    // this was also only used to track heavy reg in login component,
    //   but the userInfo.mfaVerifyMethod should equal to 'Phone'.
    // this.verifyMethod = 'Phone';

    // instead we are going to create the data object that is passed to the next
    // function, handleReceivedDataForLoginMode
    const data = {
      loginRedirect: false,
      provider: '',
      statusMsg: 'Authenticated',
      token: {
        access_token: accessToken,
        refresh_token: refreshToken,
        // these aren't necessary in this case
        expires_in: 0,
        id_token: null,
        token_type: null
      }
    } as OIDLoginWidgetExportData;

    return Promise.resolve(data);
  }

  private async receivedDataHandler(data: OIDLoginWidgetExportData): Promise<OIDLoginWidgetExportData> {
    // This function is named the same as that found in login component.
    // Original parameters: (data: OIDLoginWidgetExportData)

    // The data parameter was retrieved form the loginService.getLoginData()
    // it functioned like a gatekeeper for the login process, depending on the login mode.
    return Promise.resolve(data);
  }

  private async handleReceivedDataForLoginMode(data: OIDLoginWidgetExportData): Promise<HandleReceivedDataForLoginModeResponse> {
    let accessToken: string;

    if (typeof data.token === 'string') {
      accessToken = data.token;
    } else if (data.token && 'access_token' in data.token) {
      accessToken = data.token.access_token;
    } else {
      throw new Error('Invalid token format');
    }

    const decodedToken = this.tokenService.decodeToken(accessToken);
    const { email, phonenumber } = decodedToken;

    return Promise.resolve({
      phoneNumber: phonenumber,
      email
    });
  }

  private async getLoginData({
    email = '',
    phoneNumber = '',
    accessToken = '',
    refreshToken = '',
    ssoLogin = false,
    loginMode
  }: GetLoginDataProps): Promise<GetLoginDataResponse> {
    const user = {} as ConsumerLogin;
    user.email = email;
    user.accessToken = accessToken;
    user.refreshToken = refreshToken;
    user.loginWithSSO = ssoLogin;
    user.cellPhone = phoneNumber;

    const oldSession = this.statefulService.wrap(`${this.isOlaFlow ? 'ss:auth-ola' : 'ss:authorization'}`).valueString; // guid
    let prevSession = '';
    if (loginMode === 'verify') {
      prevSession = oldSession ?? null; // prevSession is same guid as oldSession
    }
    this.statefulService.wrap('ss:loginType').value = false;
    let comingFromHeavyReg = '';
    if (this.isBrowser) {
      comingFromHeavyReg = sessionStorage.getItem('arcDXHeavyReg'); // is null
    }

    // before the function would call getLoginDataContinued(), but we are going to pivot away from that now
    return Promise.resolve({
      oldSession,
      user,
      prevSession,
      comingFromHeavyReg
    });
  }

  private async getLoginDataContinued({ oldSession }: { oldSession: string }): Promise<void> {
    if (this.isBrowser) {
      sessionStorage.removeItem('arcDXHeavyReg');
    }
    if (this.userSession.canHookCurrentBundle && oldSession) {
      this.userService.user = new UserInfo();
      this.userService.user.token = oldSession;
    }
    // skip call consumerUserService.login() here because the user is already created
    // user and prevSession were used with consumerUserService.login(), so they were removed as parameters
    // we will not have a response from consumerUserService.login() either.
    this.userSession.canHookCurrentBundle = false;
    // 6. SetUser(response.data) // originally setUser called from this method,....
    // setUser(response.data)

    // one of these two routes below takes us to step 7. afterLogin
    // 7. afterLogin(response)
    // const loanLinkInfo = this.GetLoanLinkInfo(user);
    // if (!loanLinkInfo.loanNumber || !loanLinkInfo.borrowerId) {
    //   this.afterLogin(response);
    //   return;
    // }
    // this.consumerUserService.LinkLoan(loanLinkInfo).subscribe((): void => {
    //   this.afterLogin(response);
    // });
    return Promise.resolve();
  }

  private async setUser({
    user,
    oldSession,
    loginType
  }: {
    user: ConsumerLogin;
    oldSession: string;
    loginType?: string;
  }): Promise<{ userInfo: UserInfo }> {
    // originally was called with a parameter of reponse: LoginResponse
    const userInfo = new UserInfo();
    userInfo.email = user.email;
    userInfo.cellPhone = user.cellPhone;
    userInfo.isCellPhoneUser = !!user.cellPhone;
    // userInfo.mfaVerifyMethod should equal to 'Phone'.
    userInfo.mfaVerifyMethod = 'Phone';
    // prior was: response.credentials.access_token
    // updated would be: user.accessToken
    // however, we set it to oldSession, the guid from the previous session
    userInfo.token = oldSession;
    userInfo.token_time = new Date();
    userInfo.confetti = [];
    userInfo.registeredWithSSO = user.loginWithSSO; // response.applicationBundle.registeredWithSSO;

    // this is not in the original SetUser(), but assuming it comes back from the call
    userInfo.loginType = loginType;

    this.userService.user = userInfo;
    if (loginType?.toLowerCase() === LoanType.OLA.toLowerCase()) {
      this.userService.flowType = LoanType.OLA;
    } else if (loginType?.toLowerCase() === LoanType.OAO.toLowerCase()) {
      this.userService.flowType = LoanType.OAO;
    }

    this.userSession.startTimer();
    // prior was response.credentials.access_token
    // however, we set it to oldSession, the guid from the previous session
    this.statefulService.wrap('ss:authorization').value = oldSession;
    return Promise.resolve({ userInfo });
  }

  private async afterLogin({ userInfo, loginMode }: { userInfo: UserInfo; loginMode: 'login' | 'heavyReg' | 'verify' }): Promise<void> {
    // Originall this was called with parameter of response: Result<LoginResponse>
    if (this.userService.user?.loginType && this.userService.user.loginType.toLowerCase() === 'oao') {
      this.l360Service.setOAOLenderSettings();
    }

    // TOOD: I'm pretty sure we dont want to navigate here, perhaps elsewhere
    // if (loginMode === 'verify') {
    //   this.router.navigate([RoutingUrl.OAO.Overview]);
    // } else {
    //   this.router.navigate([RoutingUrl.Dashboard]);
    // }

    return Promise.resolve();
  }

  private async setOAOMember({ userInfo }: { userInfo: UserInfo }): Promise<void> {
    if (userInfo) {
      this.pageNavigationStateService.setStoragePrefix('oao');
      this.pageNavigationStateService.retrievePageState();
    }
    return Promise.resolve();
  }

  /** Instatouch - Personal Info */
  public getPersonalInfo({ ssn, zipCode, isPrimaryApplicant = false }: InstaTouchSession): void {
    const eventPairId = genTinyGUID(24); // TODO: Replace this with cypto.randomUUID() when TS is updated
    this.isLoading = true;
    this.errorMessage = null;
    this.logEvent('start', eventPairId);
    this.instaTouchService
      .getPersonalInformation({
        sessionId: this.instaTouchSessionId,
        zipCode: zipCode ? zipCode.toString() : '',
        last4ssn: ssn ? ssn.toString() : '',
        requestId: '',
        isPrimaryBorrower: isPrimaryApplicant,
        mobileNumber: this.mobileNumber
      })
      .subscribe({
        next: (response: ApiResult<InstaTouchUserInformation>) => {
          if (response?.isSuccess && response?.data && (response.data.personalInfo || response.data.employmentInfo)) {
            /* Converting instatouch response to JSON string and setting it as field data.*/
            const data = response.data;

            if (data.personalInfo.identity.contact.mobile && this.field.data.valueString) {
              data.personalInfo.identity.contact.mobile = this.field.data.valueString;
            }

            let promise = Promise.resolve();
            // Adding check to make sure user is not already logged in.
            if (data?.internalJwtToken?.access_token && data?.internalJwtToken?.refresh_token && !this.userService.isAuthenticated) {
              // Controls whether to show the user dropdown.
              this.userService.isExistingUser = data.isExistingUser;

              // I have the internalJwtToken, now send it to login component

              // We could grab this from the API, but we already have it in the user service
              // const loginType = this.statefulService.wrap(`${this.isOaoFlow ? 'api' : 'ola'}:loginType`).value as string;
              // Grab the flow type from BMP.FlowType in sessionStorage
              const loginType = this.userService.flowType;
              

              const { access_token: accessToken, refresh_token: refreshToken } = data.internalJwtToken;
              this.tokenService.updateToken({
                access_token: accessToken,
                refresh_token: refreshToken,
                expires_in: 0,
                id_token: null,
                token_type: null
              });

              // duplicate logic from login component
              const loginMode = 'verify';
              promise = this.setupTokenInitialize()
                .then(() => {
                  return this.setUpVerifyFlow({
                    refreshToken,
                    accessToken
                  });
                })
                .then((result: OIDLoginWidgetExportData) => {
                  return this.receivedDataHandler(result);
                })
                .then((result: OIDLoginWidgetExportData) => {
                  return this.handleReceivedDataForLoginMode(result)
                    .then((handled: HandleReceivedDataForLoginModeResponse) => {
                      return this.getLoginData({
                        loginMode,
                        email: handled.email,
                        phoneNumber: handled.phoneNumber,
                        accessToken,
                        refreshToken,
                        ssoLogin: false
                      });
                    })
                    .then((loginData: GetLoginDataResponse) => {
                      const { oldSession, user, comingFromHeavyReg } = loginData;
                      let ipromise;
                      // prevSession used to be used in getLoginDataContinued() when it called this.userService.login()
                      // isAllBackendInitialized() is false through the oao flow, compared to ola flow
                      if (oldSession && !comingFromHeavyReg && this.storageService.isAllBackendInitialized()) {
                        ipromise = this.getLoginDataContinued({ oldSession });
                      } else {
                        ipromise = this.storageService.initAllBackends().then(() => {
                          return this.getLoginDataContinued({ oldSession });
                        });
                      }
                      return ipromise
                        .then(() => {
                          return this.setUser({ user, oldSession, loginType });
                        })
                        .then(({ userInfo }: { userInfo: UserInfo }) => {
                          return this.afterLogin({ userInfo, loginMode }).then(() => {
                            if (this.userService.user?.loginType && this.userService.user.loginType.toLowerCase() === 'oao') {
                              return this.setOAOMember({ userInfo });
                            }
                          });
                        });
                    });
                })
                .then(() => {})
                .catch((error: HttpErrorResponse) => {
                  this.errorHandler(error, 'getPersonalInfo/instatouch');
                });
            }

            promise.then(() => {
              data.givenMobileNumber = this.mobileNumber;
              this.field.data.value = JSON.stringify(data);
              /* Need to be refactored */
              /* To update data, Temporaryly binding update method to click event */
              this.onClick(new Event('click'));
              this.stage = 'completed';
              this.isLoading = false;
              this.logEvent('end', eventPairId, 'success');
            });
          } else {
            this.logEvent('end', eventPairId, 'error');
            this.errorHandler({} as HttpErrorResponse, 'personalInfoAutofill/errorValidatingPersonalInfo');
          }
        },
        error: (err: HttpErrorResponse) => {
          this.errorHandler(err, 'personalInfoAutofill/errorValidatingPersonalInfo');
          this.logEvent('end', eventPairId, 'error');
        }
      });
  }

  public errorHandler(err: HttpErrorResponse, errorMessagePath: string): void {
    const errorResponse: ApiErrorResult | undefined = err?.error;
    this.errorMessage =
      errorResponse?.userMessage === undefined || errorResponse?.statusCode === '500'
        ? this.dictionary.read(errorMessagePath)
        : errorResponse.userMessage;
    this.isLoading = false;
  }

  private preCheck(mobileNumber): boolean {
    const validations = this.field.config.validationCondition;
    const preCheckFn = validations && validations.preCheck;
    if (preCheckFn) {
      return this.logicService.evaluate(
        [...preCheckFn, mobileNumber] as any,
        { field: this.field },
        true,
        this.field.iterationId,
        this.field.pageIterationId
      );
    }
    return false;
  }

  /**
   * Logs an event for the insta-touch prefill feature.
   * @param eventState The state of the event (start or end).
   * @param correlationId The correlation ID for the event.
   * @param status The status of the event (success or error).
   */
  private logEvent(eventState: 'start' | 'end', correlationId: string, status?: 'success' | 'error'): void {
    this.logger.logEvent('ecp.ui.instaTouchPrefill', {
      eventState,
      correlationId,
      status
    });
  }

  /** Capture the event from insta-touch card when user clicks manual flow link.  */
  public proceedWithoutPhonePrefill({ applicantIndex }: { applicantIndex?: number }): void {
    const proceed = () => {
      if (this.isOlaFlow) {
        this.bundleHelperService.refreshOLABundle();
      } else {
        this.bundleHelperService.refreshBundle();
      }
      this.statefulService.wrap('api:oaoMemberInformation/primaryApplicant/enableAutoFillMobileNumber').value = true;
      this.field.data.value = JSON.stringify({ isManual: true });
      this.onClick(new Event('click'));
      this.statefulService.wrap('mem:alloy-prefill-inprogress').value = false;
    };
    // If Alloy and currently waiting on link/passcode, call verify endpoint to signal skip before proceeding
    if (this.isAlloyPrefill && this.stage === 'passcode') {
      this.applicationApiService
        .verifyPrefillLink({
          verifyCode: 'skipped',
          index: applicantIndex
        })
        .subscribe({
          next: () => proceed(),
          error: () => proceed(),
          complete: () => {}
        });
      return;
    }
    proceed();
  }

  /** Cleanup polling subscription & timeout */
  public ngOnDestroy(): void {
    // this.alloyVerifyPollSub?.unsubscribe();
    // this.statefulService.wrap('mem:alloy-prefill-inprogress').value = false;

    super.ngOnDestroy?.();
  }
}
